import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { canReadTrustAccount } from '@promanage/core';
import { Button, Panel, cn } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { Icon, type IconName } from '../components/Icon';
import { EmptyState, FilterChips, ToolbarSearch } from '../features/table/ListShell';
import { useSession } from '../features/auth/session';

export const Route = createFileRoute('/reports/')({ component: ReportsPage });

type Category = 'financial' | 'property' | 'activity';
interface Report {
  to: string;
  icon: IconName;
  title: string;
  description: string;
  scope: string;
  output: string;
  /** Who is offered it, when not everybody — the same rule its route and procedure enforce. */
  requires?: (roles: readonly string[]) => boolean;
}
interface ReportGroup {
  id: Category;
  label: string;
  description: string;
  reports: Report[];
}

// Keep the directory factual: each entry opens an existing report, and labels describe its output.
const GROUPS: ReportGroup[] = [
  {
    id: 'financial',
    label: 'Financial reports',
    description: 'Owner accounts, trust balances and year-end reporting.',
    reports: [
      {
        to: '/reports/statements',
        icon: 'owners',
        title: 'Owner statement',
        description:
          'Receipts, charges and the balance held for an owner, with their property breakdown.',
        scope: 'One owner · One month',
        output: 'PDF · Print',
      },
      {
        to: '/reports/reconciliation',
        icon: 'bank',
        title: 'Trust reconciliation',
        description:
          'Bank, cash book and beneficiary balances, outstanding items and month-end sign-off.',
        scope: 'Trust account · One month',
        output: 'PDF · Print · CSV',
        requires: canReadTrustAccount,
      },
      {
        to: '/reports/tax',
        icon: 'report',
        title: 'Income and expenses, for tax',
        description:
          'Property income and expenses for an owner’s accountant, reconciled to their payouts.',
        scope: 'One owner · One tax year',
        output: 'PDF · Print',
      },
      {
        to: '/reports/matching',
        icon: 'check',
        title: 'Auto-match rate',
        description:
          'How much of the statement matched itself, what people decided instead, and which automatic matches were undone.',
        scope: 'The agency · One month',
        output: 'Print',
      },
      {
        to: '/reports/refunds',
        icon: 'swap',
        title: 'Refunds due',
        description:
          'Money held in the trust account that belongs to a tenant, split by where it came from and whether it can be paid yet.',
        scope: 'Your portfolio or the agency · As at today',
        output: 'PDF · Print',
      },
    ],
  },
  {
    id: 'property',
    label: 'Property compliance',
    description: 'Documents prepared from the property register.',
    reports: [
      {
        to: '/reports/healthy-homes',
        icon: 'property',
        title: 'Healthy Homes statement',
        description:
          'The five standards, assessment details and recorded exemptions, with missing information highlighted for review.',
        scope: 'One property · Saved assessment',
        output: 'PDF · Print',
      },
    ],
  },
  {
    id: 'activity',
    label: 'Delivery & activity',
    description: 'Track document delivery and review the agency’s recorded activity.',
    reports: [
      {
        to: '/reports/delivery',
        icon: 'calendar',
        title: 'Statement delivery',
        description:
          'Produce monthly owner statements and see which copies were sent or need attention.',
        scope: 'All owners · One month',
        output: 'Produce · Send · Track',
      },
      {
        to: '/reports/audit',
        icon: 'shield',
        title: 'System audit log',
        description:
          'Who changed what and when. Review the recorded actions behind the agency’s work.',
        scope: 'Date range · Person · Action',
        output: 'PDF · Print',
      },
      {
        to: '/reports/events',
        icon: 'clock',
        title: 'Event log',
        description:
          'What the system recorded and whether it has been processed by the event worker.',
        scope: 'Date range · Event',
        output: 'PDF · Print',
      },
    ],
  },
];

/**
 * The report directory.
 *
 * DRAWN FROM THE SAME PARTS AS EVERY LIST: the toolbar is `ToolbarSearch` and `FilterChips` in a
 * `p-4` panel, the empty state is `EmptyState`, and a card is a `Panel` that tints on hover the way
 * a clickable row or `StatTile` does. It had its own search box, its own pill buttons, Tailwind's
 * default type sizes and a blue badge on every card — each fine alone, and together a page that
 * looked like it came from another product.
 *
 * ICONS ARE NEUTRAL, not accent. Blue is the single accent (DESIGN_SYSTEM §1) and nine blue badges
 * made every card shout equally; the title turning blue on hover is the one place it belongs.
 */
function ReportsPage() {
  const [category, setCategory] = useState<Category | 'all'>('all');
  const roles = useSession()?.roles ?? [];
  // What this person is offered at all, before any filter — the count says "of" this, not of
  // reports they could never open.
  const offered = GROUPS.map((group) => ({
    ...group,
    reports: group.reports.filter((report) => report.requires?.(roles) ?? true),
  }));
  const reportCount = offered.reduce((count, group) => count + group.reports.length, 0);
  const [search, setSearch] = useState('');
  const terms = search.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  const groups = offered
    .filter((group) => category === 'all' || group.id === category)
    .map((group) => ({
      ...group,
      reports: group.reports.filter((report) => {
        const text =
          `${group.label} ${report.title} ${report.description} ${report.scope} ${report.output}`.toLocaleLowerCase();
        return terms.every((term) => text.includes(term));
      }),
    }))
    .filter((group) => group.reports.length > 0);
  const shown = groups.reduce((count, group) => count + group.reports.length, 0);
  const filtered = category !== 'all' || search.length > 0;
  const reset = () => {
    setCategory('all');
    setSearch('');
  };

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Reports"
        subtitle={
          filtered ? `Showing ${shown} of ${reportCount} reports` : `${reportCount} reports`
        }
      />

      <div className="flex flex-col gap-6">
        <Panel className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <ToolbarSearch
              value={search}
              onChange={setSearch}
              placeholder="Search reports"
              label="Search reports"
            />
            <FilterChips
              label="Report category"
              value={category}
              options={CATEGORY_OPTIONS}
              onChange={setCategory}
            />
          </div>
        </Panel>

        {groups.map((group) => (
          <section key={group.id} aria-labelledby={`reports-${group.id}`}>
            <div className="mb-3 px-1">
              <h2
                id={`reports-${group.id}`}
                className="text-[1.125rem] font-bold leading-tight tracking-[-0.014em]"
              >
                {group.label}
              </h2>
              <p className="mt-0.5 text-[0.8125rem] text-ink-muted">{group.description}</p>
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {group.reports.map((report) => (
                <ReportCard key={report.to} report={report} wide={group.reports.length === 1} />
              ))}
            </div>
          </section>
        ))}

        {shown === 0 ? (
          <Panel>
            <EmptyState
              title="No matching reports"
              action={
                <Button size="sm" variant="secondary" onClick={reset}>
                  Show all reports
                </Button>
              }
            >
              Try another name, or clear the filters.
            </EmptyState>
          </Panel>
        ) : null}
      </div>
    </div>
  );
}

const CATEGORY_OPTIONS: readonly { value: Category | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  ...GROUPS.map((group) => ({ value: group.id, label: group.label })),
];

/**
 * One report. The whole card is the link, so it carries no "Open report" of its own; what it does
 * carry is what the report is FOR and what it covers, which is what somebody picks it by.
 *
 * `wide` spans the row when a group has a single report, so one card is not left alone in a third
 * of the width.
 */
function ReportCard({ report, wide }: { report: Report; wide: boolean }) {
  return (
    <Link
      to={report.to}
      className={cn(
        'group flex h-full gap-4 rounded-lg bg-surface p-5 shadow-card transition-colors duration-[var(--duration-fast)] hover:bg-surface-sunk focus-visible:ring-2 focus-visible:ring-accent focus-visible:outline-none',
        wide && 'md:col-span-2 xl:col-span-3',
      )}
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-surface-sunk text-ink-muted ring-1 ring-rule">
        <Icon name={report.icon} size={18} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <h3 className="text-[0.9375rem] font-semibold tracking-[-0.014em] group-hover:text-accent">
          {report.title}
        </h3>
        <p className="mt-1 max-w-prose text-[0.8125rem] text-ink-muted">{report.description}</p>
        <p className="mt-auto flex flex-wrap gap-x-3 pt-3 text-[0.75rem] text-ink-subtle">
          <span>{report.scope}</span>
          <span aria-hidden="true">·</span>
          <span>{report.output}</span>
        </p>
      </div>
    </Link>
  );
}
